// src/components/ErrorBoundary.js
// Error Boundary Component with Fallback UI - Production Ready

import React from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Collapse,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { MdRefresh, MdBugReport } from 'react-icons/md';

/**
 * ErrorBoundary Class Component
 * Catches React errors and displays fallback UI
 * 
 * @example
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Send to error reporting service (optional)
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // You can also log to an error reporting service here
    // Example: Sentry.captureException(error);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          resetError: this.handleReset,
        });
      }

      // Default fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.handleReset}
          showDetails={this.state.showDetails}
          toggleDetails={this.toggleDetails}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorFallback Component
 * Default error UI displayed by ErrorBoundary
 */
const ErrorFallback = ({ 
  error, 
  errorInfo, 
  resetError, 
  showDetails, 
  toggleDetails 
}) => {
  const bgColor = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('red.200', 'red.600');
  const textColor = useColorModeValue('gray.800', 'white');

  return (
    <Box
      p={8}
      bg={bgColor}
      borderRadius="lg"
      borderWidth="2px"
      borderColor={borderColor}
      maxW="800px"
      mx="auto"
      mt={10}
    >
      <VStack spacing={6} align="stretch">
        {/* Error Alert */}
        <Alert
          status="error"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          borderRadius="md"
          py={6}
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="xl">
            Something went wrong
          </AlertTitle>
          <AlertDescription maxWidth="sm" fontSize="sm">
            An unexpected error occurred. The error has been logged and we'll look into it.
          </AlertDescription>
        </Alert>

        {/* Error Message */}
        {error && (
          <Box>
            <Heading size="sm" mb={2} color={textColor}>
              Error Message:
            </Heading>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{error.toString()}</Text>
            </Alert>
          </Box>
        )}

        {/* Action Buttons */}
        <VStack spacing={3}>
          <Button
            colorScheme="blue"
            leftIcon={<Icon as={MdRefresh} />}
            onClick={resetError}
            width="full"
          >
            Try Again
          </Button>

          <Button
            variant="outline"
            leftIcon={<Icon as={MdBugReport} />}
            onClick={toggleDetails}
            width="full"
            size="sm"
          >
            {showDetails ? 'Hide' : 'Show'} Technical Details
          </Button>
        </VStack>

        {/* Technical Details (Collapsible) */}
        <Collapse in={showDetails} animateOpacity>
          <VStack spacing={4} align="stretch">
            {/* Component Stack */}
            {errorInfo?.componentStack && (
              <Box>
                <Heading size="xs" mb={2} color={textColor}>
                  Component Stack:
                </Heading>
                <Code
                  display="block"
                  whiteSpace="pre-wrap"
                  p={4}
                  borderRadius="md"
                  fontSize="xs"
                  maxH="200px"
                  overflowY="auto"
                >
                  {errorInfo.componentStack}
                </Code>
              </Box>
            )}

            {/* Error Stack */}
            {error?.stack && (
              <Box>
                <Heading size="xs" mb={2} color={textColor}>
                  Error Stack:
                </Heading>
                <Code
                  display="block"
                  whiteSpace="pre-wrap"
                  p={4}
                  borderRadius="md"
                  fontSize="xs"
                  maxH="200px"
                  overflowY="auto"
                >
                  {error.stack}
                </Code>
              </Box>
            )}

            {/* Environment Info */}
            <Box>
              <Heading size="xs" mb={2} color={textColor}>
                Environment Info:
              </Heading>
              <Code display="block" p={4} borderRadius="md" fontSize="xs">
                <Text>Browser: {navigator.userAgent}</Text>
                <Text>Timestamp: {new Date().toISOString()}</Text>
                <Text>URL: {window.location.href}</Text>
              </Code>
            </Box>
          </VStack>
        </Collapse>

        {/* Help Text */}
        <Text fontSize="xs" color="gray.500" textAlign="center">
          If this problem persists, please contact support with the technical details above.
        </Text>
      </VStack>
    </Box>
  );
};

/**
 * withErrorBoundary HOC
 * Wraps a component with ErrorBoundary
 * 
 * @param {React.Component} Component - Component to wrap
 * @param {Object} errorBoundaryProps - Props for ErrorBoundary
 * @returns {React.Component} Wrapped component
 * 
 * @example
 * export default withErrorBoundary(MyComponent, {
 *   onError: (error, errorInfo) => {
 *     console.log('Error caught:', error);
 *   }
 * });
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WrappedComponent;
};

/**
 * ErrorBoundaryReset Component
 * Resets error boundary when key changes
 * 
 * @example
 * <ErrorBoundaryReset resetKeys={[userId]}>
 *   <UserProfile userId={userId} />
 * </ErrorBoundaryReset>
 */
export class ErrorBoundaryReset extends React.Component {
  state = { resetKey: 0 };

  componentDidUpdate(prevProps) {
    const { resetKeys = [] } = this.props;
    const { resetKeys: prevResetKeys = [] } = prevProps;

    if (
      resetKeys.length !== prevResetKeys.length ||
      resetKeys.some((key, index) => key !== prevResetKeys[index])
    ) {
      this.setState(state => ({ resetKey: state.resetKey + 1 }));
    }
  }

  render() {
    return (
      <ErrorBoundary key={this.state.resetKey} {...this.props}>
        {this.props.children}
      </ErrorBoundary>
    );
  }
}

export default ErrorBoundary;